import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './ChartV2.module.scss';
import {
  DEFAULT_HORIZONS,
  DEFAULT_INTERVALS,
  DEFAULT_PANE_ID,
} from './constants';
import {
  measureYAxisColumnWidth,
  resolveThemeFromElement,
  Y_AXIS_COLUMN_WIDTH,
} from './engine/draw';
import {
  DEFAULT_CHART_PALETTE,
  paletteColorAt,
  resolveCssVarColor,
  resolveSeriesPalette,
} from './engine/palette';
import { computeYExtent } from './engine/scales';
import { useHorizonInterval } from './hooks/useHorizonInterval';
import { useLiveFeed } from './hooks/useLiveFeed';
import { usePaneUIState } from './hooks/usePaneUIState';
import { useSeriesMeta } from './hooks/useSeriesMeta';
import { useSeriesVisibility } from './hooks/useSeriesVisibility';
import { useSharedDomain } from './hooks/useSharedDomain';
import { PaneDivider } from './internals/PaneDivider';
import { PaneView } from './internals/PaneView';
import {
  ChartV2Handle,
  ChartV2Insight,
  ChartV2LegendEntry,
  ChartV2PriceDisplay,
  ChartV2Props,
  ChartV2ResolvedSeries,
  ChartV2SeriesType,
} from './types/chart-v2.types';
import { buildDefaultLegendMenu, seriesHasOhlc } from './ui/legend-menu';
import { ViewController } from './ui/ViewController';
import {
  computeDataDisabledHorizons,
  computeDataExtent,
  computeInitialDomain,
  computePanExtent,
} from './utils/data-extent';
import { formatPrice } from './utils/format';
import { groupByPane, type Pane, seriesPaneId } from './utils/panes';
import { themeToCssVars } from './utils/theme-vars';

/**
 * Measure the y-axis column width needed to fit every pane's widest tick
 * label, floored at `Y_AXIS_COLUMN_WIDTH`. Shared across panes so plot
 * widths — and therefore the x-axis / crosshair — stay aligned. Tick values
 * fall within each series' visible extent, so the formatted extent bounds
 * are a safe upper bound for the widest label.
 */
const computeAxisColumnWidth = (
  el: HTMLElement | null,
  panes: Pane<ChartV2ResolvedSeries>[],
  sharedDomain: [number, number]
): number => {
  if (!el) {
    return Y_AXIS_COLUMN_WIDTH;
  }

  const { fontMono } = resolveThemeFromElement(el);
  const labels: string[] = [];

  for (const pane of panes) {
    for (const s of pane.series) {
      const extent = computeYExtent([s], sharedDomain);

      labels.push(formatPrice(extent.min), formatPrice(extent.max));
    }
  }

  return measureYAxisColumnWidth(fontMono, labels);
};

/**
 * Canvas-driven, standalone chart component. Composes a top bar
 * (`ViewController`), one or more panes (`ChartCanvas`), an optional
 * legend, and live-tick wiring into a single drop-in chart.
 *
 * Required: `series`. Most other props (horizon, interval, palette,
 * insights, callbacks) are optional with sensible defaults — pass them
 * to control the chart from the host or to observe interactions.
 *
 * For the raw drawing surface without the chrome, use `ChartCanvas`.
 * For the top-bar selectors only, use `ViewController`.
 */
export const ChartV2 = (props: ChartV2Props) => {
  const {
    ref,
    series,
    horizon: controlledHorizon,
    interval: controlledInterval,
    defaultHorizon = '1Y',
    defaultInterval = '1D',
    availableHorizons = DEFAULT_HORIZONS,
    availableIntervals = DEFAULT_INTERVALS,
    disabledHorizons,
    enabledIntervals,
    defaultHiddenSeriesIds,
    defaultPriceDisplayByPane,
    theme,
    locales,
    className,
    renderLegend,
    showDefaultLegend = true,
    onHorizonChange,
    onIntervalChange,
    onSeriesTypeChange,
    onDomainChange,
    onSeriesToggle,
    onSeriesMovePane,
    onPaneMove,
    onPaneCollapse,
    showCollapse = false,
    showMaximize = false,
    onPaneMaximize,
    onPaneRemove,
    onClearAll,
    onRemoveSeries,
    colors = DEFAULT_CHART_PALETTE,
    liveFeed,
    insights,
  } = props;

  const defaultPriceDisplayKey = useMemo(
    () => JSON.stringify(defaultPriceDisplayByPane ?? {}),
    [defaultPriceDisplayByPane]
  );
  const defaultPriceDisplayState = useMemo(
    () => defaultPriceDisplayByPane ?? {},
    [defaultPriceDisplayKey]
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const firstCanvasRef = useRef<ChartV2Handle>(null);

  // Horizon / interval — controlled when props given, uncontrolled otherwise.
  const { horizon, interval, handleHorizonChange, handleIntervalChange } =
    useHorizonInterval({
      controlledHorizon,
      controlledInterval,
      defaultHorizon,
      defaultInterval,
      onHorizonChange,
      onIntervalChange,
    });

  // Visibility — hide / remove tracking.
  const {
    hiddenSeriesIds,
    removedSeriesIds,
    handleSeriesToggle,
    removeSeriesIds,
  } = useSeriesVisibility({ defaultHiddenSeriesIds, onSeriesToggle });

  // Per-series meta (color / seriesType / paneIndex) + move handlers.
  const {
    seriesMeta,
    handleSeriesColorChange,
    handlePerSeriesTypeChange,
    handleSeriesMovePane,
    handlePaneMove,
  } = useSeriesMeta({
    series,
    onSeriesTypeChange,
    onSeriesMovePane,
    onPaneMove,
  });

  // Streaming live feed.
  const liveBySeries = useLiveFeed(liveFeed);

  // Bump on theme change so palette-dependent memos re-resolve CSS vars.
  const [paletteTick, setPaletteTick] = useState(0);

  useLayoutEffect(() => {
    setPaletteTick(t => t + 1);
  }, [theme]);

  const themeStyle = useMemo(() => themeToCssVars(theme), [theme]);
  const rootClassName = [styles.root, className].filter(Boolean).join(' ');

  // Effective series — layer meta + live updates on top of caller's props.
  // Palette assignment is owned by chartv2; consumers don't supply colors.
  const effectiveSeries = useMemo<ChartV2ResolvedSeries[]>(() => {
    const palette = rootRef.current
      ? resolveSeriesPalette(rootRef.current)
      : [];

    return series
      .filter(s => !removedSeriesIds.has(s.id))
      .map((s, i) => {
        const meta = seriesMeta[s.id];
        const live = liveBySeries[s.id];
        const data =
          live && live.points.length > 0 ? [...s.data, ...live.points] : s.data;
        const rawColor = meta?.color ?? paletteColorAt(palette, i);
        const resolvedColor =
          resolveCssVarColor(rootRef.current, rawColor) ?? rawColor;
        const resolvedType: ChartV2SeriesType =
          meta?.seriesType ?? s.seriesType;
        const paneIndex = meta?.paneIndex ?? 0;

        return {
          ...s,
          color: resolvedColor,
          seriesType: resolvedType,
          paneId: s.paneId ?? `pane-${paneIndex}`,
          data,
        };
      });
  }, [series, seriesMeta, liveBySeries, paletteTick, removedSeriesIds]);

  const visibleSeries = useMemo(
    () => effectiveSeries.filter(s => !hiddenSeriesIds.has(s.id)),
    [effectiveSeries, hiddenSeriesIds]
  );

  const dataExtent = useMemo(
    () => computeDataExtent(visibleSeries),
    [visibleSeries]
  );
  const effectiveDisabledHorizons = useMemo(() => {
    const next = new Set([
      ...computeDataDisabledHorizons(dataExtent, availableHorizons),
      ...(disabledHorizons ?? []),
    ]);

    return Array.from(next);
  }, [dataExtent, availableHorizons, disabledHorizons]);
  const panExtent = useMemo(() => computePanExtent(dataExtent), [dataExtent]);
  const initialDomain = useMemo(
    () => computeInitialDomain(horizon, dataExtent),
    [horizon, dataExtent]
  );

  const { sharedDomain, handleDomainChange, handleDomainReset } =
    useSharedDomain({
      horizon,
      dataExtent,
      initialDomain,
      onDomainChange,
    });

  const [sharedHoverTime, setSharedHoverTime] = useState<number | null>(null);
  // Per-pane price-display mode (`price` | `indexed`). Internal state
  // only — consumers don't need to wire this to succeed, but we could
  // expose a callback in the future for URL persistence.
  const [priceDisplayByPane, setPriceDisplayByPane] = useState<
    Record<string, ChartV2PriceDisplay>
  >(() => defaultPriceDisplayState);

  useEffect(() => {
    setPriceDisplayByPane(defaultPriceDisplayState);
  }, [defaultPriceDisplayState]);

  const setPaneDisplay = useCallback(
    (paneId: string, next: ChartV2PriceDisplay) => {
      setPriceDisplayByPane(prev => ({ ...prev, [paneId]: next }));
    },
    []
  );

  const handleResetChart = useCallback(() => {
    handleDomainReset();
    setPriceDisplayByPane(defaultPriceDisplayState);
  }, [handleDomainReset, defaultPriceDisplayState]);

  // Group visible series into panes and set up pane-level UI state (collapse,
  // maximize, fractions, divider drag).
  const panes = useMemo(() => groupByPane(visibleSeries), [visibleSeries]);

  const legendPanes = useMemo(
    () => groupByPane(effectiveSeries),
    [effectiveSeries]
  );

  // Shared y-axis column count *and* width across panes so plot widths match
  // (keeps the x-axis / crosshair aligned). Width fits the widest tick label
  // so long values aren't clipped; `paletteTick` re-resolves the font on mount.
  const { count: sharedAxisColumnCount, width: axisColumnWidth } = useMemo(
    () => ({
      count: panes.reduce((max, pane) => {
        const effective =
          priceDisplayByPane[pane.id] === 'indexed' ? 1 : pane.series.length;

        return Math.max(max, effective);
      }, 1),
      width: computeAxisColumnWidth(rootRef.current, panes, sharedDomain),
    }),
    [panes, priceDisplayByPane, sharedDomain, paletteTick]
  );

  const {
    paneFractions,
    collapsedPanes,
    maximizedPaneId,
    toggleCollapsed,
    toggleMaximized,
    clearCollapsedFor,
    setMaximizedIfMatch,
    startDividerDrag,
  } = usePaneUIState({
    paneCount: panes.length,
    bodyRef,
    onPaneCollapse,
    onPaneMaximize,
  });

  useImperativeHandle(
    ref,
    () => ({
      resetZoom: handleDomainReset,
      setDomain: range => handleDomainChange(range),
      exportImage: () => firstCanvasRef.current?.exportImage(),
    }),
    [handleDomainChange, handleDomainReset]
  );

  // Top chart-type chip bar → fan the change out to every series. We
  // don't maintain a separate "global override" state because the
  // per-series dropdown can flip individual series afterwards — the
  // override would win and silently block the per-series writes.
  const handleGlobalSeriesTypeChange = useCallback(
    (next: ChartV2SeriesType) => {
      for (const s of series) {
        handlePerSeriesTypeChange(s.id, next);
      }
    },
    [series, handlePerSeriesTypeChange]
  );

  const handlePaneRemove = useCallback(
    (paneId: string) => {
      const victims = visibleSeries
        .filter(s => seriesPaneId(s) === paneId)
        .map(s => s.id);

      removeSeriesIds(victims);

      if (onRemoveSeries) {
        victims.forEach(id => onRemoveSeries(id));
      }

      clearCollapsedFor(paneId);
      setMaximizedIfMatch(paneId);
      onPaneRemove?.(paneId);
    },
    [
      visibleSeries,
      removeSeriesIds,
      onRemoveSeries,
      clearCollapsedFor,
      setMaximizedIfMatch,
      onPaneRemove,
    ]
  );

  // Pane index lookup for the more-menu move-up/down enablement.
  const paneIdsOrder = useMemo(() => panes.map(p => p.id), [panes]);
  const lastPaneIndex = paneIdsOrder.length - 1;

  const buildEntry = useCallback(
    (s: ChartV2ResolvedSeries): ChartV2LegendEntry => {
      const paneId = s.paneId ?? DEFAULT_PANE_ID;
      const paneOrderIndex = paneIdsOrder.indexOf(paneId);
      const sameInPane = effectiveSeries.filter(
        other => seriesPaneId(other) === paneId
      ).length;
      const canMoveUp = paneOrderIndex > 0;
      const canMoveDown = paneOrderIndex < lastPaneIndex || sameInPane > 1;

      return {
        series: s,
        hidden: hiddenSeriesIds.has(s.id),
        moreMenuItems: buildDefaultLegendMenu({
          seriesId: s.id,
          color: s.color,
          seriesType: s.seriesType,
          hasOhlc: seriesHasOhlc(s),
          canMoveUp,
          canMoveDown,
          colors,
          onColorChange: color => handleSeriesColorChange(s.id, color),
          onSeriesTypeChange: type => handlePerSeriesTypeChange(s.id, type),
          onMovePane: direction => handleSeriesMovePane(s.id, direction),
          labels: {
            changeColor: locales?.legendMenuChangeColor,
            moveUp: locales?.legendMenuMoveUp,
            moveDown: locales?.legendMenuMoveDown,
            seriesTypeLabel: {
              line: locales?.legendMenuSeriesTypeLine,
              area: locales?.legendMenuSeriesTypeArea,
              candlestick: locales?.legendMenuSeriesTypeCandlestick,
              bar: locales?.legendMenuSeriesTypeBar,
            },
          },
        }),
      };
    },
    [
      effectiveSeries,
      hiddenSeriesIds,
      paneIdsOrder,
      lastPaneIndex,
      colors,
      handleSeriesColorChange,
      handlePerSeriesTypeChange,
      handleSeriesMovePane,
    ]
  );

  const insightsByPane = useMemo(() => {
    const map = new Map<string, ChartV2Insight[]>();

    if (!insights || insights.length === 0) {
      return map;
    }

    const primaryPaneId = panes[0]?.id;

    for (const insight of insights) {
      const paneId = insight.paneId ?? primaryPaneId;

      if (!paneId) {
        continue;
      }

      const bucket = map.get(paneId);

      if (bucket) {
        bucket.push(insight);
        continue;
      }

      map.set(paneId, [insight]);
    }

    return map;
  }, [insights, panes]);

  const legendEntriesByPane = useMemo(() => {
    const map = new Map<string, ChartV2LegendEntry[]>();

    for (const pane of legendPanes) {
      map.set(pane.id, pane.series.map(buildEntry));
    }

    return map;
  }, [legendPanes, buildEntry]);

  const allLegendEntries = useMemo<ChartV2LegendEntry[]>(
    () => effectiveSeries.map(buildEntry),
    [effectiveSeries, buildEntry]
  );

  const hasContent = effectiveSeries.some(s => s.data.length > 0);
  const hasOhlc = effectiveSeries.some(s => s.data.some(p => 'close' in p));
  const disabledSeriesTypes: ChartV2SeriesType[] = hasOhlc
    ? []
    : ['candlestick', 'bar'];

  // `selected` feeds the chip bar's "currently active" highlight. With
  // per-series overrides possible, derive it from the first series'
  // resolved type (what the primary asset is actually rendered as) so
  // the chip stays in sync with reality.
  const primarySeriesType: ChartV2SeriesType =
    effectiveSeries[0]?.seriesType ?? 'line';

  // Per-series items feed the chart-type dropdown when more than one
  // series is plotted — each row gets its own toggle so the user can
  // change types for a specific asset without touching the others.
  const chartTypeSeriesItems = useMemo(
    () =>
      effectiveSeries.map(s => ({
        id: s.id,
        label: s.legendLabel ?? s.label,
        seriesType: s.seriesType,
        disabled: !seriesHasOhlc(s),
      })),
    [effectiveSeries]
  );

  return (
    <div
      ref={rootRef}
      className={rootClassName}
      style={themeStyle}
      data-testid="chartv2-root"
    >
      <ViewController
        horizons={availableHorizons}
        selectedHorizon={horizon}
        onHorizonChange={handleHorizonChange}
        disabledHorizons={effectiveDisabledHorizons}
        intervals={availableIntervals}
        selectedInterval={interval}
        onIntervalChange={handleIntervalChange}
        enabledIntervals={enabledIntervals}
        selectedSeriesType={primarySeriesType}
        onSeriesTypeChange={handleGlobalSeriesTypeChange}
        disabledSeriesTypes={disabledSeriesTypes}
        chartTypeSeriesItems={chartTypeSeriesItems}
        onPerSeriesTypeChange={handlePerSeriesTypeChange}
        locales={locales}
        onClearAll={hasContent ? onClearAll : undefined}
        clearAllLabel={locales?.clearAll}
        // Match the v1 chart's empty/initial-load behaviour: the
        // horizon, interval and chart-type controls are inert until
        // at least one series has datapoints. Prevents users from
        // tweaking them before the first data frame lands and getting
        // confused by a "selection" that doesn't render anything.
        disabled={!hasContent}
      />
      <div className={styles.body} data-testid="chartv2-body" ref={bodyRef}>
        <div className={styles.panes}>
          {panes.map((pane, idx) => {
            const isHiddenByMaximize =
              maximizedPaneId !== null && maximizedPaneId !== pane.id;

            if (isHiddenByMaximize) {
              return <React.Fragment key={pane.id} />;
            }

            const isCollapsed = collapsedPanes.has(pane.id);
            const isMaximized = maximizedPaneId === pane.id;
            const isLast = idx === panes.length - 1;
            const fraction = isCollapsed
              ? 0
              : (paneFractions[idx] ?? 1 / panes.length);
            const isXAxisPane = maximizedPaneId ? isMaximized : isLast;
            const canShowDivider = !isLast && !isMaximized;
            const paneEntries = legendEntriesByPane.get(pane.id) ?? [];

            return (
              <React.Fragment key={pane.id}>
                <PaneView
                  pane={pane}
                  index={idx}
                  axisColumnCount={sharedAxisColumnCount}
                  axisColumnWidth={axisColumnWidth}
                  paneCount={panes.length}
                  fraction={fraction}
                  isCollapsed={isCollapsed}
                  isMaximized={isMaximized}
                  isXAxisPane={isXAxisPane}
                  paneEntries={paneEntries}
                  allLegendEntries={allLegendEntries}
                  renderLegend={renderLegend}
                  showDefaultLegend={showDefaultLegend}
                  locales={locales}
                  sharedDomain={sharedDomain}
                  panExtent={panExtent}
                  sharedHoverTime={sharedHoverTime}
                  showLatestPrice={!!liveFeed}
                  insights={insightsByPane.get(pane.id)}
                  canvasRef={firstCanvasRef}
                  priceDisplay={priceDisplayByPane[pane.id] ?? 'price'}
                  onPriceDisplayChange={next => setPaneDisplay(pane.id, next)}
                  onResetChart={handleResetChart}
                  onHoverTimeChange={setSharedHoverTime}
                  onDomainChange={handleDomainChange}
                  onSeriesToggle={handleSeriesToggle}
                  onRemoveSeries={onRemoveSeries}
                  onPaneMoveUp={() =>
                    handlePaneMove(pane.id, 'up', visibleSeries)
                  }
                  onPaneMoveDown={() =>
                    handlePaneMove(pane.id, 'down', visibleSeries)
                  }
                  onPaneCollapse={
                    showCollapse ? () => toggleCollapsed(pane.id) : undefined
                  }
                  onPaneMaximize={
                    showMaximize ? () => toggleMaximized(pane.id) : undefined
                  }
                  onPaneRemove={() => handlePaneRemove(pane.id)}
                />
                {canShowDivider && (
                  <PaneDivider index={idx} onPointerDown={startDividerDrag} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
